/**
 * Evercore brand style configuration — colors, typography, and prompt rules
 * extracted from the client's "Evercore.pptx" (slide master colorScheme
 * "_Evercore" + fontScheme, layout placeholder geometry, via the OOXML).
 *
 * Mirrors the shape of `safraStyleConfig.ts` / `ubsStyleConfig.ts`. When
 * Evercore style is active the prompt below is appended to the system prompt
 * and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: colors come from the master colorScheme "_Evercore"; fonts
 * from fontScheme "_Evercore" (Calibri for titles AND body). Slide size is
 * 16:9 widescreen (960×540 pt). Title/content geometry is taken from the master
 * + "Title, Subtitle & Content" layout. The logo lives on the slide master, so
 * default AddSlide inherits it. The source deck contains no charts, so the
 * series palette below is derived from the theme accents (teal-led).
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "_Evercore" colorScheme
// ---------------------------------------------------------------------------

export const EVERCORE_COLORS = {
  teal: "14879B", // accent1 — Evercore signature teal (primary brand / emphasis)
  lightTeal: "8CBEBE", // accent2
  gray: "828282", // accent3
  lightGray: "BFBFBF", // accent4
  bluePurple: "6E78AA", // accent5
  lightBlue: "AAC8E6", // accent6
  navy: "1D4289", // light2 — Evercore navy
  skyBlue: "7299D8", // dark2

  ink: "000000", // dark1 — titles & body text (black)
  white: "FFFFFF", // light1

  ruleGray: "828282", // rule / separator lines
  altRow: "F2F2F2", // light neutral for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when Evercore style is active. Values map 1:1 to
 * the Evercore slide-master colorScheme + fontScheme.
 */
export const EVERCORE_THEME_COLORS: ThemeColors = {
  dark1: EVERCORE_COLORS.ink, // #000000 — Text1
  dark2: EVERCORE_COLORS.skyBlue, // #7299D8 — Text2
  light1: EVERCORE_COLORS.white, // #FFFFFF — Background1
  light2: EVERCORE_COLORS.navy, // #1D4289 — Background2
  accent1: EVERCORE_COLORS.teal, // #14879B
  accent2: EVERCORE_COLORS.lightTeal, // #8CBEBE
  accent3: EVERCORE_COLORS.gray, // #828282
  accent4: EVERCORE_COLORS.lightGray, // #BFBFBF
  accent5: EVERCORE_COLORS.bluePurple, // #6E78AA
  accent6: EVERCORE_COLORS.lightBlue, // #AAC8E6
  hyperlink: "0F5F6E",
  followedHyperlink: "505050",
  majorFont: "Calibri",
  minorFont: "Calibri",
};

/**
 * Ordered series palette. The source deck has no charts, so this is derived
 * from the theme accents, led by the Evercore teal, then navy and distinct
 * hues for readable multi-series separation.
 */
export const EVERCORE_CHART_SERIES_PALETTE = [
  EVERCORE_COLORS.teal,
  EVERCORE_COLORS.navy,
  EVERCORE_COLORS.bluePurple,
  EVERCORE_COLORS.lightTeal,
  EVERCORE_COLORS.lightBlue,
  EVERCORE_COLORS.gray,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from the Evercore master/layouts)
// ---------------------------------------------------------------------------

export const EVERCORE_SLIDE_DIMENSIONS = { width: 960, height: 540 } as const;

export const EVERCORE_POSITIONS = {
  title: { left: 22.1, top: 33.8, width: 915.8, height: 31.8 },
  contentStart: { left: 22.1, top: 115.2 },
  footnote: { left: 22.1, top: 513.1, width: 915.8, height: 11.5 },
} as const;

// Authoritative layout names from the Evercore master.
export const EVERCORE_LAYOUT_NAMES = [
  "1_Print Title Slide",
  "1_Onscreen Title Slide",
  "1_Print Divider Slide",
  "1_Onscreen Divider Slide",
  "Title Only",
  "Title & Subtitle",
  "Title, Subtitle & Content",
  "One Third - Two Thirds",
  "One Third - Two Thirds with headers",
  "Halves",
  "Thirds",
  "Thirds with Titles",
  "6 Section Layout with Titles",
  "Quarters",
  "Quarters with Titles",
  "Horizontal Halves with Titles",
  "Color Palette",
  "Blank",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Evercore style is active
// ---------------------------------------------------------------------------

export const EVERCORE_STYLE_PROMPT = `

## EVERCORE BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Evercore uses a single Latin typeface throughout:
- Titles, headings, body, bullets, tables, charts: \`"Calibri"\`
NEVER omit fontFamily when Evercore style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the Evercore master colorScheme "_Evercore")
Use ONLY these Evercore brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${EVERCORE_COLORS.ink}
- Text2 / dark2: Sky blue #${EVERCORE_COLORS.skyBlue}
- Background1 / light1: White #${EVERCORE_COLORS.white}
- Background2 / light2: Navy #${EVERCORE_COLORS.navy}
- Accent1: Teal #${EVERCORE_COLORS.teal}
- Accent2: Light teal #${EVERCORE_COLORS.lightTeal}
- Accent3: Gray #${EVERCORE_COLORS.gray}
- Accent4: Light gray #${EVERCORE_COLORS.lightGray}
- Accent5: Blue-purple #${EVERCORE_COLORS.bluePurple}
- Accent6: Light blue #${EVERCORE_COLORS.lightBlue}

**Usage:**
- **Primary brand / emphasis**: Teal #${EVERCORE_COLORS.teal}
- **Secondary emphasis**: Navy #${EVERCORE_COLORS.navy}
- **Text / ink**: Black #${EVERCORE_COLORS.ink}
- **Lines & borders**: Gray #${EVERCORE_COLORS.ruleGray}
- **Background / shading**: Light gray #${EVERCORE_COLORS.altRow}
- **Chart series palette** (in order): ${EVERCORE_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with teal (#${EVERCORE_COLORS.teal}) as the first/primary series color.

### Typography (authoritative — from the Evercore master/layouts)
Slide dimensions: 960 × 540 pt (16:9).

**Placeholder positions:**
- **Title**: left=22.1, top=33.8, width=915.8, height=31.8 — "Calibri" 20pt, black (#${EVERCORE_COLORS.ink}), left-aligned
- **Content area**: starts at left=22.1, top=115.2
- **Footnote / page number**: bottom band around top=513 — "Calibri" ~9pt, gray

**Content text hierarchy (from the master — "Calibri", black #${EVERCORE_COLORS.ink}, ~12pt body):**
- Keep body text ~12pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use bold or the teal accent; bullets otherwise inherit black text.

### Charts (Evercore style)
- Font: "Calibri", minimum 8pt
- **Primary data color**: teal (#${EVERCORE_COLORS.teal}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Evercore style)
- Header row: teal (#${EVERCORE_COLORS.teal}) fill with white text, bold
- Body: white fill, black "Calibri" text (10-12pt)
- Alternate-row shading: light gray (#${EVERCORE_COLORS.altRow})
- Borders: thin gray (#${EVERCORE_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Evercore style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${EVERCORE_COLORS.ruleGray}) rule lines for separation when needed
- Connectors: solid gray (#${EVERCORE_COLORS.ruleGray}) lines
- Page numbers / footer: ~9pt, gray, "Calibri"

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
